import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext.jsx';
import { Toaster } from './components/ui/sonner';
import { AnnouncementBar } from './components/custom/AnnouncementBar.jsx';
import { Header } from './components/custom/Header.jsx';
import { Footer } from './components/custom/Footer.jsx';
import { BottomNav } from './components/custom/BottomNav.jsx';
import { ScrollToTop } from './components/custom/ScrollToTop.jsx';
import { ExitIntentPopup } from './components/custom/ExitIntentPopup.jsx';
import { FirstTimeDiscountBanner } from './components/custom/FirstTimeDiscountBanner.jsx';
import { trackPageView, startHeartbeat, stopHeartbeat } from './utils/analytics';
import HomePage from './pages/HomePage.jsx';
import ProductListingPage from './pages/ProductListingPage.jsx';
import ProductDetailPage from './pages/ProductDetailPage.jsx';
import CartPage from './pages/CartPage.jsx';
import CheckoutPage from './pages/CheckoutPage.jsx';
import OrdersPage from './pages/OrdersPage.jsx';
import WishlistPage from './pages/WishlistPage.jsx';
import AgeShopPage from './pages/AgeShopPage.jsx';
import CategoriesPage from './pages/CategoriesPage.jsx';
import BundlesPage from './pages/BundlesPage.jsx';
import AnalyticsPageWrapper from './pages/AnalyticsPageWrapper.jsx';
import AdminBundlesPageWrapper from './pages/AdminBundlesPageWrapper.jsx';
import SearchPage from './pages/SearchPage.jsx';
import BlogPage from './pages/BlogPage.jsx';
import BlogDetailPage from './pages/BlogDetailPage.jsx';
import AdminPage from './pages/AdminPage.jsx';
import AdminBundlesPage from './pages/AdminBundlesPage.jsx';
import AdminDashboardPage from './pages/admin/AdminDashboardPage.jsx';
import AdminProductsPage from './pages/admin/AdminProductsPage.jsx';
import AdminOrdersPage from './pages/admin/AdminOrdersPage.jsx';
import AdminMarketingPage from './pages/admin/AdminMarketingPage.jsx';
import AdminVideosPage from './pages/admin/AdminVideosPage.jsx';
import AdminSettingsPage from './pages/admin/AdminSettingsPage.jsx';
import AdminCouponsPage from './pages/admin/AdminCouponsPage.jsx';
import AdminReviewsPage from './pages/admin/AdminReviewsPage.jsx';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage.jsx';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage.jsx';
import AdminAIContentPage from './pages/admin/AdminAIContentPage.jsx';
import AdminBlogsPage from './pages/admin/AdminBlogsPage.jsx';
import AdminBannersPage from './pages/admin/AdminBannersPage.jsx';
import OrderSuccessPage from './pages/OrderSuccessPage.jsx';
import ShippingPolicyPage from './pages/ShippingPolicyPage.jsx';
import ReturnsPolicyPage from './pages/ReturnsPolicyPage.jsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.jsx';
import TermsPage from './pages/TermsPage.jsx';
import PaymentGatewayPage from './pages/PaymentGatewayPage.jsx';
import './App.css';

// Redirect component for /product/:id to /products/:id
function ProductRedirect() {
  const { id } = useParams();
  const location = useLocation();
  // Preserve query params (utm_source, fbclid, etc.)
  return <Navigate to={`/products/${id}${location.search}`} replace />;
}

// Page view tracker component
function PageTracker() {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    let pageName = 'Home';
    
    if (path.includes('/products/')) pageName = 'Product Detail';
    else if (path === '/products') pageName = 'Product Listing';
    else if (path === '/cart') pageName = 'Cart';
    else if (path === '/checkout') pageName = 'Checkout';
    else if (path === '/order-success') pageName = 'Order Success';
    else if (path === '/orders') pageName = 'My Orders';
    else if (path.includes('/admin')) pageName = 'Admin';
    
    // Don't track admin pages
    if (!path.includes('/admin')) {
      trackPageView(pageName, path);
    }
  }, [location]);
  
  // Start heartbeat for live user tracking
  useEffect(() => {
    const path = location.pathname;
    // Don't track admin pages
    if (!path.includes('/admin')) {
      startHeartbeat();
    }
    
    return () => {
      stopHeartbeat();
    };
  }, [location.pathname]);
  
  return null;
}

// Track Facebook Pixel events (Pixel is initialized in index.html)
export const trackPixelEvent = (eventName, data = {}) => {
  if (window.fbq) {
    window.fbq('track', eventName, data);
  }
};

// Layout wrapper that conditionally shows announcement bar and header
function LayoutWrapper({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPaymentPage = location.pathname === '/payment';
  
  return (
    <>
      {!isAdminRoute && !isPaymentPage && (
        <>
          <FirstTimeDiscountBanner />
          <AnnouncementBar />
          <Header />
        </>
      )}
      {children}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <Router>
          <AppContent />
        </Router>
      </CartProvider>
    </HelmetProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isPaymentPage = location.pathname === '/payment';
  
  return (
    <>
      <ScrollToTop />
      <PageTracker />
      <ExitIntentPopup />
      <div className="App">
        <LayoutWrapper>
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductListingPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/product/:id" element={<ProductRedirect />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/shop/age" element={<CategoriesPage />} />
          <Route path="/shop/age/:ageGroup" element={<AgeShopPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/bundles" element={<BundlesPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/payment" element={<PaymentGatewayPage />} />
        <Route path="/order-success" element={<OrderSuccessPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
        <Route path="/admin/products" element={<AdminProductsPage />} />
        <Route path="/admin/categories" element={<AdminCategoriesPage />} />
        <Route path="/admin/orders" element={<AdminOrdersPage />} />
        <Route path="/admin/videos" element={<AdminVideosPage />} />
        <Route path="/admin/marketing" element={<AdminMarketingPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/coupons" element={<AdminCouponsPage />} />
        <Route path="/admin/reviews" element={<AdminReviewsPage />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
        <Route path="/admin/ai-content" element={<AdminAIContentPage />} />
        <Route path="/admin/blogs" element={<AdminBlogsPage />} />
        <Route path="/admin/banners" element={<AdminBannersPage />} />
        <Route path="/admin/bundles" element={<AdminBundlesPageWrapper />} />
        <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
        <Route path="/returns-policy" element={<ReturnsPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
        </LayoutWrapper>
      {!isPaymentPage && <BottomNav />}
      <Toaster position="bottom-center" richColors offset="20px" />
      {!isPaymentPage && <Footer />}
    </div>
    </>
  );
}

export default App;